import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBackOutlined as BackIcon,
  ContentCopyOutlined as CopyIcon,
  OpenInNewOutlined as OpenIcon,
} from '@mui/icons-material';
import { getIntegrationCatalog, PROMPTCREW_MCP_URL, getIntegrationById, getPromptCrewMcpUrl } from '../../content/integrationCatalog';
import IntegrationLogo from './IntegrationLogo';
import PromptCrewBrandName from './PromptCrewBrandName';
import { buildConnectionOnboardingHandoff, buildFirstCapabilityHandoff, buildOnboardingMcpHandoff } from '../../utils/onboardingMcpHandoff';
import IntegrationOnboarding from './IntegrationOnboarding';

const IntegrationSetupDetails = ({ initialPlatform = 'chatgpt', onBack, onDone, compact = false, onboardingCapability = null, onboardingCapabilities = [] }) => {
  const [selectedId, setSelectedId] = useState(initialPlatform);
  const [copiedMessage, setCopiedMessage] = useState('');
  const [setupMode, setSetupMode] = useState('ask');
  const [showRequest, setShowRequest] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [journeyStep, setJourneyStep] = useState(1);
  const [requestCopied, setRequestCopied] = useState(false);
  const [discoveryCopyFailed, setDiscoveryCopyFailed] = useState(false);
  const discoveryCapability = onboardingCapabilities.find((item) => Number(item.step) === 2);
  const discoveryRequest = buildFirstCapabilityHandoff(discoveryCapability, window.location.origin);
  const copyDiscoveryRequest = async () => {
    try {
      await navigator.clipboard.writeText(discoveryRequest);
      setDiscoveryCopyFailed(false);
      setCopyError(false);
      setCopiedMessage(`Copied. Paste the discovery request into ${platformName} to get started.`);
    } catch {
      setDiscoveryCopyFailed(true);
      setCopyError(true);
      setCopiedMessage('Could not copy. Select the discovery request below to copy it.');
    }
  };
  const selectJourneyStep = (step) => {
    setJourneyStep(step);
    setSetupMode('ask');
    setShowRequest(false);
    setRequestCopied(false);
    setCopiedMessage('');
  };
  const integration = useMemo(() => getIntegrationById(selectedId, window.location.origin), [selectedId]);
  const integrationCatalog = getIntegrationCatalog(window.location.origin);
  const mcpUrl = getPromptCrewMcpUrl(window.location.origin);
  const selectedCapability = onboardingCapabilities.find((item) => Number(item.step) === journeyStep) || (journeyStep === 1 ? onboardingCapability : null);
  const onboardingRequest = selectedCapability
    ? journeyStep === 1
      ? buildConnectionOnboardingHandoff(selectedCapability, window.location.origin, integration.setupPrompt)
      : buildOnboardingMcpHandoff(selectedCapability, window.location.origin)
    : '';
  const platformName = integration.id === 'chatgpt' ? 'ChatGPT or Codex' : integration.id === 'mcp' ? 'your AI tool' : integration.name;
  const setupRequest = onboardingRequest || integration.setupPrompt.replace(PROMPTCREW_MCP_URL, mcpUrl);
  const directoryConnection = Boolean(integration.actionUrl) && (integration.id === 'chatgpt' || integration.id === 'claude');

  const copyMcpUrl = async () => {
    try {
      await navigator.clipboard.writeText(mcpUrl);
      setCopyError(false);
      setCopiedMessage('PromptCrew MCP URL copied');
    } catch (error) {
      console.error('Could not copy PromptCrew MCP URL:', error);
      setCopyError(true);
      setCopiedMessage('Could not copy. Select the MCP URL to copy it.');
    }
  };

  const copySetupPrompt = async () => {
    try {
      await navigator.clipboard.writeText(setupRequest);
      setRequestCopied(true);
      setCopyError(false);
      setCopiedMessage(`Copied. Paste this into ${platformName} and send it.`);
    } catch (error) {
      console.error('Could not copy PromptCrew setup request:', error);
      setCopyError(true);
      setRequestCopied(false);
      setShowRequest(true);
      setCopiedMessage('Could not copy. Select the setup message below to copy it.');
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, ml: 0, mr: 'auto', minWidth: 0 }}>
      {compact && onboardingCapability && (
        <Box component="ol" aria-label="Your guided setup journey" sx={{ m: 0, mb: 3, p: 0, pb: 2.5, borderBottom: 1, borderColor: 'divider', listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
          {[
            ['Connect and verify', 'Give your AI tool access to your Library and Assistant.'],
            ['Discover repeatable work', 'Find useful workflows and capabilities worth reusing.'],
            ['Plan your integrations', 'Get a setup plan for your role and the tools you use.'],
          ].map(([title, detail], index) => (
            <Box component="li" key={title} sx={{ minWidth: 0 }}>
              <Button aria-label={title} disabled={index > 0 && !onboardingCapabilities.some((item) => Number(item.step) === index + 1)} aria-pressed={journeyStep === index + 1} onClick={() => selectJourneyStep(index + 1)} sx={{ p: 0, minHeight: 44, gap: 1.25, textAlign: 'left', justifyContent: 'flex-start', alignItems: 'baseline', color: journeyStep === index + 1 ? 'primary.main' : 'text.primary', fontWeight: 700, fontSize: '0.9375rem', borderBottom: 2, borderColor: journeyStep === index + 1 ? 'primary.main' : 'transparent', borderRadius: 0 }}><Box component="span" aria-hidden="true" sx={{ flexShrink: 0 }}>{index + 1}.</Box>{title}</Button>
              <Typography variant="caption" color="text.primary" sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.5 }}>{detail}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {compact && onboardingCapability && <Typography variant="body2" sx={{ mb: 2.5, color: 'text.primary' }}>Start with Step 1. Your AI tool will ask before continuing. Already connected? Choose Step 2 or 3 above.</Typography>}
      {journeyStep > 1 && selectedCapability && <Box sx={{ py: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.125rem' }}>{selectedCapability.title}</Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.primary', maxWidth: 900 }}>{journeyStep === 2 ? 'Choose the conversations, saved prompts, email, documents, or other connected sources you want to review. Your AI looks for recurring tasks and reusable work, then searches your Library, team, and Community for existing capabilities that fit.' : 'Find an integration plan for your role and tools, then shape a reusable onboarding SOP.'}</Typography>
        {journeyStep === 2 && <Typography variant="body2" sx={{ mt: 1.5, color: 'text.primary', maxWidth: 900 }}>You get a ranked shortlist of workflows to reuse, adapt, or build. Choose one to develop together and review it before anything is saved. No source connections yet? Start with pasted examples or describe tasks you repeat.</Typography>}
        {journeyStep === 2 && <Typography variant="body2" sx={{ mt: 1.5, color: 'text.primary', maxWidth: 900 }}>Try your new capability on a real task, refine the result, and identify useful supporting knowledge. Then build another or move on to your integration plan.</Typography>}
        {journeyStep === 3 && <Typography variant="body2" sx={{ mt: 1.5, color: 'text.primary', maxWidth: 900 }}>Start with an existing onboarding SOP for your role, if one is available. Otherwise, review your current tools, prioritize useful connections, and approve a reusable setup guide to share with your team.</Typography>}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Paste this request into the same connected AI conversation to keep your earlier context.</Typography>
        <Button variant="contained" startIcon={<CopyIcon />} onClick={copySetupPrompt} sx={{ mt: 2 }}>Copy Step {journeyStep} request</Button>
        <Button onClick={() => setShowRequest((value) => !value)} aria-expanded={showRequest} sx={{ mt: 2, ml: 1 }}>{showRequest ? 'Hide message' : 'View message'}</Button>
        {showRequest && <Typography variant="body2" sx={{ mt: 2, p: 2, bgcolor: 'action.hover', color: 'text.primary', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', userSelect: 'text' }}>{onboardingRequest}</Typography>}
      </Box>}
      {onBack && (
        <Button startIcon={<BackIcon />} onClick={onBack} sx={{ mb: 1.5, ml: -1 }}>
          Back
        </Button>
      )}
      {journeyStep === 1 && <Box sx={{ display: 'grid', width: '100%', minWidth: 0, gridTemplateColumns: { xs: '1fr', md: '220px minmax(0, 1fr)' }, gap: { xs: 2, md: 3 } }}>
        <List aria-label="AI platforms" disablePadding sx={{ width: '100%', borderRight: { md: 1 }, borderColor: 'divider', pr: { md: 2 }, ...(compact && { display: { xs: 'flex', md: 'block' }, overflowX: { xs: 'auto', md: 'visible' }, gap: 0.75, pb: { xs: 1, md: 0 } }) }}>
          {integrationCatalog.map((item) => (
            <ListItemButton
              key={item.id}
              selected={item.id === integration.id}
              onClick={() => {
                setSelectedId(item.id);
                setSetupMode('ask');
                setShowRequest(false);
                setCopiedMessage('');
                setRequestCopied(false);
              }}
              sx={{ px: 1.5, py: 1.25, mb: 0.75, borderRadius: 1.5, alignItems: 'center', gap: 1.25, ...(compact && { flexShrink: 0, flexGrow: 0, whiteSpace: { xs: 'nowrap', md: 'normal' } }) }}
            >
              {item.logo && <IntegrationLogo brand={item.logo} alt="" size={20} />}
              <ListItemText
                primary={item.name}
                secondary={compact ? undefined : item.eyebrow}
                primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9375rem' }}
                secondaryTypographyProps={{ variant: 'caption', ...(compact && { sx: { display: { xs: 'none', md: 'block' } } }) }}
              />
            </ListItemButton>
          ))}
        </List>

        <Box sx={{ minWidth: 0, py: { md: 0.75 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.15rem' }}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
              {integration.logo && <IntegrationLogo brand={integration.logo} alt="" size={compact ? 24 : 30} />}
              <Box component="span">Use <PromptCrewBrandName /> in {integration.name}</Box>
            </Box>
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 680, ...(compact && { fontSize: '1rem', lineHeight: 1.6 }) }}>
            {integration.summary}
          </Typography>
          {!compact && <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.75 }}>
            <Chip label={integration.availability} size="small" sx={{ fontWeight: 600 }} />
            {integration.pluginComingSoon && <Chip label="Native plugin coming soon" size="small" variant="outlined" sx={{ fontWeight: 600 }} />}
          </Box>}

          {directoryConnection ? <Box sx={{ mt: 2.5 }}>
            <Button variant="contained" endIcon={<OpenIcon />} onClick={() => window.open(integration.actionUrl, '_blank', 'noopener,noreferrer')}>{integration.actionLabel}</Button>
            <Typography variant="body2" color="text.primary" sx={{ mt: 1.5 }}>Complete sign-in to your PromptCrew account, then enable PromptCrew in your AI tool.</Typography>
            <Box component="ol" sx={{ mt: 2, pl: 2.5, display: 'grid', gap: 1, color: 'text.primary' }}>
              {integration.manualSteps.map((step) => <Typography component="li" variant="body2" color="text.primary" key={step}>{step}</Typography>)}
            </Box>
            {integration.id === 'claude' && <Typography variant="body2" color="text.primary" sx={{ mt: 1.5 }}>Custom connector fallback: {mcpUrl}. Use PromptCrew as the name and leave OAuth client ID and secret blank.</Typography>}
          </Box> : <>
          <Tabs value={setupMode} onChange={(_, value) => setSetupMode(value)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label="Connection method" sx={{ mt: 2.5, borderBottom: 1, borderColor: 'divider', ...(compact && { '& .MuiTab-root': { fontSize: '1rem', minHeight: 52, textTransform: 'none', letterSpacing: 0 } }) }}>
            <Tab id="integration-ask-tab" aria-controls="integration-setup-panel" value="ask" label={compact ? 'Guided setup' : `Ask ${integration.id === 'mcp' ? 'your AI tool' : integration.name}`} />
            <Tab id="integration-manual-tab" aria-controls="integration-setup-panel" value="manual" label={onboardingRequest ? 'Step-by-step setup' : 'Set up manually'} />
          </Tabs>

          <Box id="integration-setup-panel" role="tabpanel" aria-labelledby={`integration-${setupMode}-tab`} sx={{ pt: 2.25, maxWidth: 960, overflowWrap: 'anywhere' }}>
            {setupMode === 'ask' ? (
              <Box sx={{ p: 1.75, bgcolor: 'primary.50', border: 1, borderColor: 'primary.100', borderRadius: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {onboardingRequest ? `Start your guided setup in ${integration.id === 'mcp' ? 'your AI tool' : integration.name}` : `Start by asking ${integration.id === 'mcp' ? 'your AI tool' : integration.name}`}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75, color: 'text.primary', lineHeight: 1.6 }}>
                  {onboardingRequest
                    ? `Copy the setup message, open ${platformName}, and paste it into a new conversation. Send it to begin. Your AI will guide the connection and verify access before continuing.`
                    : `Copy this request, paste it into ${integration.id === 'mcp' ? 'your AI tool' : integration.name}, and let it guide the connection.`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
                  <Button variant="contained" size={onboardingRequest ? 'medium' : 'small'} startIcon={<CopyIcon />} onClick={copySetupPrompt} sx={onboardingRequest ? { px: 3, minHeight: 48 } : undefined}>
                    {onboardingRequest ? 'Copy setup message' : 'Copy setup request'}
                  </Button>
                  {integration.actionUrl && <Button variant="outlined" size="small" onClick={() => window.open(integration.actionUrl, '_blank', 'noopener,noreferrer')} endIcon={<OpenIcon />}>{integration.actionLabel}</Button>}
                </Box>
                {onboardingRequest && <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>You complete sign-in yourself. The guide asks before moving on to workflow discovery or creating capabilities.</Typography>}
                <Button size="small" onClick={() => setShowRequest((value) => !value)} aria-expanded={showRequest} aria-controls="integration-message-preview" sx={{ mt: 1 }}>{showRequest ? 'Hide message' : 'View message'}</Button>
                {showRequest && <Typography id="integration-message-preview" variant="body2" sx={{ mt: 1, p: 1.25, bgcolor: 'background.paper', border: 1, borderColor: 'primary.100', borderRadius: 1, color: 'text.primary', lineHeight: 1.55, whiteSpace: 'pre-wrap', userSelect: 'text' }}>
                  {setupRequest}
                </Typography>}
              </Box>
            ) : (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Set up <PromptCrewBrandName />
                </Typography>
                <Typography variant="body2" color={compact ? 'text.primary' : 'text.secondary'} sx={{ mb: 1.5, lineHeight: 1.55 }}>
                  {integration.manualConfiguration.length ? 'Use these steps if your AI tool cannot add the connection from chat.' : 'Install the plugin, then connect your PromptCrew account.'}
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 1.15 }}>
                  {integration.manualSteps.map((step) => (
                    <Typography component="li" key={step} variant="body2" color={compact ? 'text.primary' : 'text.secondary'} sx={{ pl: 0.5, lineHeight: 1.55 }}>
                      {step}
                    </Typography>
                  ))}
                </Box>
                {integration.manualConfiguration.length > 0 && <Box sx={{ mt: 2.25, border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{ display: 'block', px: 1.5, py: 1, fontWeight: 700, bgcolor: 'action.hover' }}>
                    Enter these details
                  </Typography>
                  {integration.manualConfiguration.map((field, index) => (
                    <Box key={field.label} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '190px minmax(0, 1fr)' }, gap: 1, px: 1.5, py: 1.15, borderTop: index ? 1 : 0, borderColor: 'divider' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{field.label}</Typography>
                      {field.value === 'mcp-url' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <Typography variant="body2" color={compact ? 'text.primary' : 'text.secondary'} sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere', flex: 1 }}>
                            {mcpUrl}
                          </Typography>
                          <Tooltip title="Copy MCP URL">
                            <IconButton aria-label="Copy PromptCrew MCP URL" onClick={copyMcpUrl} size="small">
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="body2" color={compact ? 'text.primary' : 'text.secondary'} sx={{ overflowWrap: 'anywhere' }}>
                          {field.value}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>}
                {onboardingRequest && <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography sx={{ fontWeight: 700 }}>After connecting</Typography>
                  <Typography variant="body2" color={compact ? 'text.primary' : 'text.secondary'} sx={{ mt: 0.5 }}>Return to Guided setup and copy the message to verify access and start your onboarding capabilities.</Typography>
                  <Button onClick={() => setSetupMode('ask')} sx={{ mt: 1 }}>Continue to guided setup</Button>
                </Box>}
              </>
            )}
          </Box>

          </>}
          <Box sx={{ mt: 1, display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
            {!directoryConnection && setupMode === 'manual' && integration.actionUrl && (
              <Button variant="outlined" onClick={() => window.open(integration.actionUrl, '_blank', 'noopener,noreferrer')} endIcon={<OpenIcon />}>
                {integration.actionLabel}
              </Button>
            )}
            {onDone && <Button onClick={onDone}>I will finish this later</Button>}
          </Box>
          {!compact && <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 3, pt: 2, maxWidth: 960 }}>
            <Typography sx={{ fontWeight: 700 }}>Once connected, discover reusable workflows</Typography>
            <Typography sx={{ mt: 1, lineHeight: 1.6, color: 'text.primary' }}>Find repeatable work in your history or start with an everyday task. Paste the request into your connected AI tool to get started.</Typography>
            <Button variant="outlined" startIcon={<CopyIcon />} onClick={copyDiscoveryRequest} sx={{ mt: 1.5 }}>Copy discovery request</Button>
            {discoveryCopyFailed && <Typography sx={{ mt: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', userSelect: 'text', color: 'text.primary' }}>{discoveryRequest}</Typography>}
          </Box>}
        </Box>
      </Box>}
      {requestCopied && <Box role="status" sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography sx={{ fontWeight: 700 }}>Your request is copied</Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.primary' }}>Paste it into {journeyStep === 1 ? platformName : 'the same AI conversation'} and send it. Continue here when you are ready; copying does not run the capability.</Typography>
        {journeyStep < 3 && onboardingCapabilities.some((item) => Number(item.step) === journeyStep + 1) && <Button onClick={() => selectJourneyStep(journeyStep + 1)} sx={{ mt: 1 }}>Ready for Step {journeyStep + 1}</Button>}
      </Box>}
      <Snackbar open={Boolean(copiedMessage)} autoHideDuration={4000} onClose={() => setCopiedMessage('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setCopiedMessage('')} severity={copyError ? 'error' : 'success'} sx={{ width: '100%' }}>
          {copiedMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default function IntegrationSetup(props) {
  return props.compact ? <IntegrationOnboarding {...props} /> : <IntegrationSetupDetails {...props} />;
}
